import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { CircleHelpIcon, PlusIcon } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type JSX,
} from 'react';

import { Button } from '@/components/ui/button.js';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs.js';
import { AddMemoryDialog } from '@/features/memory/AddMemoryDialog.js';
import { AutoSavedMemoryTab } from '@/features/memory/AutoSavedMemoryTab.js';
import { MemoryCardsTab } from '@/features/memory/MemoryCardsTab.js';
import { MemoryCheckHistoryDialog } from '@/features/memory/MemoryCheckHistoryDialog.js';
import { ensureMemoryEnabled } from '@/features/memory/guard.js';
import { MemoryDocumentTab } from '@/features/memory/MemoryDocumentTab.js';
import { MemoryHistoryTab } from '@/features/memory/MemoryHistoryTab.js';
import { MemoryScopeSwitcher } from '@/features/memory/MemoryScopeSwitcher.js';
import {
  agentGuideEntryUrl,
  memoryGuideUrl,
  type MemoryGuideSection,
} from '@/lib/guide-links.js';
import { MemorySuggestionsTab } from '@/features/memory/MemorySuggestionsTab.js';
import { MemoryViewSwitcher } from '@/features/memory/MemoryViewSwitcher.js';
import { RevisionConditionDialog } from '@/features/memory/RevisionConditionDialog.js';
import {
  MEMORY_MAP_SCOPES,
  MemoryMapTab,
  type MemoryMapScope,
} from '@/features/memory/map/index.js';
import { MemoryRemoveDialog } from '@/features/memory/MemoryRemoveDialog.js';
import {
  blockTitle,
  canEditMemoryBlock,
  canWriteMemory,
} from '@/features/memory/memory-display.js';
import { useMemoryCheckNaming } from '@/features/memory/useMemoryCheckNaming.js';
import { memoryNavVisible } from '@/features/memory/memory-nav.js';
import { toMemoryMapNodes } from '@/features/memory/map-nodes.js';
import {
  defaultMemoryTab,
  defaultMemoryView,
  parseMemorySearch,
  MEMORY_TABS,
  type MemoryTab,
  type MemoryView,
} from '@/features/memory/route-search.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import { resolveCurrentUserRole } from '@/features/workspace/workspace-access.js';
import { autoSavedMemoryQueryOptions } from '@/lib/api/auto-saved-memory.js';
import {
  MEMORY_SCOPES,
  memoryBlocksQueryOptions,
  pendingMemoryProposalsQueryOptions,
} from '@/lib/api/memory.js';
import {
  workspaceMembersQueryOptions,
  workspaceQueryOptions,
} from '@/lib/api/workspaces.js';
import { PageContainer } from '@/ui/PageContainer.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

/** The agent guide entry that explains the Auto-saved tab, owned by cti-website. */
const AUTO_SAVED_GUIDE_ENTRY = 'manage-agent-memory';

export const Route = createFileRoute('/memory')({
  component: MemoryPage,
  beforeLoad: () => ensureMemoryEnabled(),
  validateSearch: parseMemorySearch,
});

/**
 * The Memory section: the map, the document, the inbox and the history of one workspace's memory,
 * for one person.
 *
 * Two documents live behind these four tabs — the workspace's shared memory and the caller's own —
 * and which one they are showing is the scope in the address bar. There is **one** scope switcher for
 * the whole section (canon §16.3): the tab that is open renders it, the map offers `All memory` as a
 * third setting because a graph can draw both documents at once, and every setting writes the same
 * `?scope=`.
 *
 * The Memory tab holds two views of the one document — the memories as cards, and the Markdown behind
 * them — kept in `?view=` rather than as a fifth tab, because they share the scope, the character
 * budget and everything else the tabs do not.
 */
function MemoryPage(): JSX.Element {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const activeTab: MemoryTab = search.tab ?? defaultMemoryTab();
  const activeView: MemoryView = search.view ?? defaultMemoryView();

  /**
   * The guide section the question mark leads to — the one that explains what is on screen.
   * `document` carries two views, and they are two different sections of the guide.
   */
  const guideSection: MemoryGuideSection | null =
    activeTab === 'auto-saved'
      ? null
      : activeTab === 'document'
        ? activeView === 'source'
          ? 'source'
          : 'cards'
        : activeTab;
  /** The Auto-saved tab is the agent's own store, described by its own entry of the agent guide. */
  const guideHref =
    guideSection === null
      ? agentGuideEntryUrl(AUTO_SAVED_GUIDE_ENTRY)
      : memoryGuideUrl(guideSection);

  const selectedWorkspaceId = useUiStore((state) => state.selectedWorkspaceId);
  const workspaceId = selectedWorkspaceId ?? '';
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );

  const workspaceQuery = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: selectedWorkspaceId !== null,
  });
  const membersQuery = useQuery({
    ...workspaceMembersQueryOptions(workspaceId),
    enabled: selectedWorkspaceId !== null,
  });
  /**
   * Whether this is the person's own auto-provisioned workspace. It has one member, so no chat in it
   * is ever read by anybody else — and shared memory is memory a team keeps. There is nothing for a
   * second document to hold here, so the section shows one, and the controls for choosing between the
   * two are not drawn at all.
   */
  const isPersonalWorkspace = workspaceQuery.data?.kind === 'personal';
  const scope = isPersonalWorkspace ? 'personal' : (search.scope ?? 'shared');
  const mapScopes = isPersonalWorkspace
    ? (['personal'] as const)
    : MEMORY_MAP_SCOPES;

  /**
   * The shared document is not read until the registry has said what kind of workspace this is. An
   * unresolved workspace reads as "not personal", so firing on it would send exactly the request a
   * personal workspace must not send, on every first paint.
   *
   * Pending proposals are read in one call that covers both memories, and it stays that way here. In a
   * personal workspace the service now refuses to file anything against shared memory at all — that
   * workspace has none — so the answer simply never carries a shared suggestion, and the inbox needs no
   * rule of its own to keep one out.
   */
  const sharedBlocks = useQuery({
    ...memoryBlocksQueryOptions(selectedWorkspaceId, 'shared'),
    enabled: workspaceQuery.data !== undefined && !isPersonalWorkspace,
  });
  const personalBlocks = useQuery(
    memoryBlocksQueryOptions(selectedWorkspaceId, 'personal'),
  );
  const proposalsQuery = useQuery(
    pendingMemoryProposalsQueryOptions(selectedWorkspaceId),
  );
  /**
   * Read when the page opens rather than when the tab is clicked: every read is an agent call, and
   * the first one in a while pays the agent's cold start.
   */
  useQuery(autoSavedMemoryQueryOptions());

  const role = resolveCurrentUserRole({
    workspace: workspaceQuery.data,
    members: membersQuery.data,
    currentUserId: identity.identity?.userId ?? null,
    isAdmin: false,
  });
  const canWrite = canWriteMemory(scope, role);

  const blocks = useMemo(
    () => [...(sharedBlocks.data ?? []), ...(personalBlocks.data ?? [])],
    [sharedBlocks.data, personalBlocks.data],
  );
  const nodes = useMemo(() => toMemoryMapNodes(blocks), [blocks]);
  /**
   * Names for the ids a check run carries, asked for only once a run has actually happened — the map
   * panel puts them in the line that says through whose connection the check went (canon §11).
   */
  const checkNaming = useMemoryCheckNaming(
    workspaceId,
    blocks.some((block) => block.lastCheckRun !== null),
  );

  /**
   * Whether the map is drawing both documents at once.
   *
   * `?scope=` names a document, and `All memory` names none — so choosing it on the map leaves the
   * address bar alone and the Memory, Suggestions and History tabs stay on the document they were
   * already showing. The asymmetry is deliberate: `all` is meaningful for a graph and meaningless for
   * an editor, a history list or the save that follows them, which act on exactly one document.
   * A `?scope=` in the address wins over it, so a shared link opens the map on the scope it names.
   */
  const [mapShowsEverything, setMapShowsEverything] = useState(
    search.scope === undefined,
  );

  /**
   * A link naming one memory puts the map on that memory's own document. `All memory` is a setting the
   * address bar cannot hold (see above), so a link that survived a client-side navigation would
   * otherwise leave the map on whatever it was showing and quietly ignore the `?scope=` beside the
   * block it was asked to open.
   */
  useEffect(() => {
    if (search.block !== undefined) setMapShowsEverything(false);
  }, [search.block]);

  const [adding, setAdding] = useState(false);
  /**
   * The block whose revision condition is being set, by id rather than by value: the list refetches
   * after the patch lands, and holding the block itself would leave the dialog showing the condition
   * as it was before the save.
   */
  const [conditionBlockId, setConditionBlockId] = useState<string | null>(null);
  const conditionBlock =
    blocks.find((block) => block.id === conditionBlockId) ?? null;
  /** The memory whose check history is open, held by id for the same reason as the condition above. */
  /**
   * Whether the map may offer a removal on one node — the same right the card resolves per block, since
   * the map holds nodes rather than blocks and cannot answer it itself.
   */
  const canRemoveMemory = useCallback(
    (nodeId: string) => {
      const block = blocks.find((candidate) => candidate.id === nodeId);
      if (block === undefined) return false;
      return canEditMemoryBlock(block, role, identity.identity?.userId ?? null);
    },
    [blocks, role, identity.identity?.userId],
  );

  /** The memory the map asked to remove, or null. The graph names it; this is what runs it. */
  const [removeBlockId, setRemoveBlockId] = useState<string | null>(null);
  const removeBlock =
    blocks.find((block) => block.id === removeBlockId) ?? null;

  const [checkHistoryBlockId, setCheckHistoryBlockId] = useState<string | null>(
    null,
  );
  const checkHistoryBlock =
    blocks.find((block) => block.id === checkHistoryBlockId) ?? null;

  /** The one scope control's one handler, whichever tab rendered it. */
  function chooseScope(next: MemoryMapScope): void {
    setMapShowsEverything(next === 'all');
    if (next === 'all') return;
    void navigate({
      search: (previous) => ({ ...previous, scope: next }),
    });
  }

  function goToNode(nodeId: string, tab: MemoryTab): void {
    const node = nodes.find((candidate) => candidate.id === nodeId);
    void navigate({
      search: (previous) => ({ ...previous, tab, scope: node?.scope ?? scope }),
    });
  }

  /**
   * Open one memory for editing, from the map.
   *
   * This used to drop the person into the whole document with no anchor at all — 16,000 characters and
   * no indication of which line was the one they clicked. It now names the memory in the address, which
   * the cards read to open and scroll to it.
   */
  function goToBlock(nodeId: string): void {
    const node = nodes.find((candidate) => candidate.id === nodeId);
    void navigate({
      search: (previous) => ({
        ...previous,
        tab: 'document',
        view: 'cards',
        block: nodeId,
        scope: node?.scope ?? scope,
      }),
    });
  }

  if (selectedWorkspaceId === null) {
    return (
      <PageContainer maxWidth="5xl" className="gap-5">
        <p className="text-sm text-muted-foreground">
          Pick a workspace to see its memory.
        </p>
      </PageContainer>
    );
  }

  if (!memoryNavVisible(workspaceQuery.data?.domains)) {
    return (
      <PageContainer maxWidth="5xl" className="gap-5">
        <h1 className="font-heading text-xl font-bold tracking-tight">
          Memory
        </h1>
        <p className="text-sm text-muted-foreground">
          Memory is switched off in this workspace. A workspace owner can turn
          it back on in workspace settings.
        </p>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="5xl" className="gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="font-heading text-xl font-bold tracking-tight">
            Memory
          </h1>
          {/** Same affordance a dialog's header carries, on a page that has no dialog to host it. */}
          <a
            data-testid="memory-guide-link"
            href={guideHref}
            target="_blank"
            rel="noopener noreferrer"
            title="Open the memory guide"
            aria-label="Open the memory guide"
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <CircleHelpIcon className="size-4" aria-hidden="true" />
          </a>
        </div>
        <Button onClick={() => setAdding(true)}>
          <PlusIcon />
          {canWrite ? 'Add memory' : 'Propose a change'}
        </Button>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) =>
          void navigate({
            search: (previous) => ({
              ...previous,
              tab: (MEMORY_TABS as readonly string[]).includes(value)
                ? (value as MemoryTab)
                : 'map',
            }),
          })
        }
      >
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="map">Map</TabsTrigger>
          <TabsTrigger value="document">Memory</TabsTrigger>
          <TabsTrigger value="suggestions">
            Suggestions
            {(proposalsQuery.data?.length ?? 0) > 0
              ? ` (${proposalsQuery.data?.length})`
              : ''}
          </TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
          <TabsTrigger value="auto-saved">Auto-saved</TabsTrigger>
        </TabsList>

        <TabsContent value="map">
          <MemoryMapTab
            nodes={nodes}
            workspaceName={workspaceQuery.data?.name ?? ''}
            scope={
              isPersonalWorkspace
                ? 'personal'
                : mapShowsEverything
                  ? 'all'
                  : scope
            }
            scopes={mapScopes}
            selectedNodeId={search.block ?? null}
            onScopeChange={chooseScope}
            onEdit={goToBlock}
            onHistory={(nodeId) => goToNode(nodeId, 'history')}
            onEditCondition={(nodeId) => setConditionBlockId(nodeId)}
            onRemove={(nodeId) => setRemoveBlockId(nodeId)}
            canRemove={canRemoveMemory}
            checkNaming={checkNaming}
            onCheckHistory={(nodeId) => setCheckHistoryBlockId(nodeId)}
          />
        </TabsContent>

        <TabsContent value="document" tabIndex={-1}>
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-3">
              {!isPersonalWorkspace && (
                <MemoryScopeSwitcher
                  scope={scope}
                  scopes={MEMORY_SCOPES}
                  onScopeChange={chooseScope}
                />
              )}
              <MemoryViewSwitcher
                view={activeView}
                onViewChange={(next) =>
                  void navigate({
                    search: (previous) => ({ ...previous, view: next }),
                  })
                }
              />
            </div>
            {activeView === 'cards' ? (
              <MemoryCardsTab
                workspaceId={selectedWorkspaceId}
                scope={scope}
                role={role}
                currentUserId={identity.identity?.userId ?? null}
                selectedBlockId={search.block ?? null}
                onCheckHistory={(blockId) => setCheckHistoryBlockId(blockId)}
                onEditCondition={(blockId) => setConditionBlockId(blockId)}
                onOpenSuggestions={() =>
                  void navigate({
                    search: (previous) => ({
                      ...previous,
                      tab: 'suggestions',
                    }),
                  })
                }
              />
            ) : (
              <MemoryDocumentTab
                workspaceId={selectedWorkspaceId}
                scope={scope}
                canWrite={canWrite}
                onPropose={() => setAdding(true)}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="suggestions">
          <MemorySuggestionsTab workspaceId={selectedWorkspaceId} role={role} />
        </TabsContent>

        <TabsContent value="history">
          <div className="flex flex-col gap-3">
            {!isPersonalWorkspace && (
              <MemoryScopeSwitcher
                scope={scope}
                scopes={MEMORY_SCOPES}
                onScopeChange={chooseScope}
              />
            )}
            <MemoryHistoryTab
              workspaceId={selectedWorkspaceId}
              scope={scope}
              canRollback={canWrite}
            />
          </div>
        </TabsContent>

        <TabsContent value="auto-saved">
          <AutoSavedMemoryTab />
        </TabsContent>
      </Tabs>

      {adding && (
        <AddMemoryDialog
          workspaceId={selectedWorkspaceId}
          scope={scope}
          canConfirm={canWrite}
          onClose={() => setAdding(false)}
        />
      )}

      {conditionBlock !== null && (
        <RevisionConditionDialog
          workspaceId={selectedWorkspaceId}
          block={conditionBlock}
          onClose={() => setConditionBlockId(null)}
        />
      )}

      {removeBlock !== null && (
        <MemoryRemoveDialog
          workspaceId={selectedWorkspaceId}
          memory={removeBlock}
          canApprove={canWriteMemory(removeBlock.scope, role)}
          onClose={() => setRemoveBlockId(null)}
        />
      )}

      {checkHistoryBlock !== null && (
        <MemoryCheckHistoryDialog
          workspaceId={selectedWorkspaceId}
          blockId={checkHistoryBlock.id}
          blockTitle={blockTitle(checkHistoryBlock.contentMarkdown)}
          naming={checkNaming}
          onClose={() => setCheckHistoryBlockId(null)}
        />
      )}
    </PageContainer>
  );
}
